import api from './api';

export interface Emissora {
  id: number;
  nome: string;
  rede?: string;
  meio?: string;
  cidade?: string;
  estado?: string;
  prefixo?: string;
  codigo?: string;
  valor?: number;
  grupos?: string;
  streamurl?: string;
  gravar?: boolean;
  diassemana?: string;
  horainicio?: string;
  horafim?: string;
}

export interface EmissoraCreate {
  nome: string;
  rede?: string;
  meio?: string;
  cidade?: string;
  estado?: string;
  prefixo?: string;
  codigo?: string;
  valor?: number;
  grupos?: string;
  streamurl?: string;
  gravar?: boolean;
  diassemana?: string;
  horainicio?: string;
  horafim?: string;
}

export interface EmissoraUpdate {
  nome?: string;
  rede?: string;
  meio?: string;
  cidade?: string;
  estado?: string;
  prefixo?: string;
  codigo?: string;
  valor?: number;
  grupos?: string;
  streamurl?: string;
  gravar?: boolean;
  diassemana?: string;
  horainicio?: string;
  horafim?: string;
}

export interface EmissoraFilter {
  nome?: string;
  meio?: string;
  estado?: string;
}

export const EmissorasService = {
  getEmissoras: async (params: EmissoraFilter, skip: number = 0, limit: number = 10): Promise<Emissora[]> => {
    try {
      console.log('[EmissorasService] Attempting to getEmissoras.');
      
      // Create the request parameters
      const requestParams = { skip, limit, ...params };
      console.log('[EmissorasService] Request params:', requestParams);

      // CRITICAL FIX: Use a relative path that inherits the page's protocol
      // This is the key to avoiding mixed content errors
      const endpoint = '/emissoras';
      
      // Log before making the request
      console.log(`[EmissorasService] Making request to relative endpoint: ${endpoint}`);
      console.log(`[EmissorasService] Current page protocol: ${window.location.protocol}`);
      
      // Make the request using the relative path
      const response = await api.get(endpoint, { 
        params: requestParams,
        baseURL: '/api/v1' // Force using a relative baseURL
      });
      
      // For debugging - log the actual request details
      console.log('[EmissorasService] Response received!');
      
      // Check if we got a successful response with data
      if (response.data) {
        console.log('[EmissorasService] Emissoras received successfully:', 
          Array.isArray(response.data) ? `${response.data.length} items` : 'data object');
        return response.data;
      } else {
        console.warn('[EmissorasService] Received empty response data');
        return [];
      }
    } catch (error: any) {
      console.error('[EmissorasService] Error fetching emissoras:', error);
      
      // Enhanced error logging for debugging mixed content issues
      if (error.response) {
        console.error('[EmissorasService] Error Response:', error.response.data);
        console.error('[EmissorasService] Error Status:', error.response.status);
      } else if (error.request) {
        // Error with request - likely mixed content or connection issue
        console.error('[EmissorasService] Network Error Details:');
        console.error(`- Request URL: ${error.config?.url || 'unknown'}`);
        console.error(`- Base URL: ${error.config?.baseURL || 'unknown'}`);
        console.error(`- Full URL: ${error.config?.baseURL}${error.config?.url}`);
        console.error(`- Method: ${error.config?.method || 'unknown'}`);
        
        // Check for mixed content issues
        const fullUrl = `${error.config?.baseURL || ''}${error.config?.url || ''}`;
        if (fullUrl.startsWith('http:') && window.location.protocol === 'https:') {
          console.error('⚠️ MIXED CONTENT ISSUE DETECTED: Trying to make HTTP request from HTTPS page');
          console.error('Solution: Ensure all URLs are either relative or use HTTPS protocol');
        }
      }
      
      // Return empty array instead of throwing to prevent UI from breaking completely
      console.error('[EmissorasService] Returning empty array due to error');
      return [];
    }
  },

  getEmissora: async (id: number) => {
    const response = await api.get(`/emissoras/${id}`);
    return response.data;
  },

  createEmissora: async (emissora: EmissoraCreate) => {
    const response = await api.post('/emissoras', emissora);
    return response.data;
  },

  updateEmissora: async (id: number, emissora: EmissoraUpdate) => {
    const response = await api.put(`/emissoras/${id}`, emissora);
    return response.data;
  },

  deleteEmissora: async (id: number) => {
    const response = await api.delete(`/emissoras/${id}`);
    return response.data;
  },

  getMeios: async () => {
    const response = await api.get('/emissoras/meios');
    return response.data;
  },

  getEstados: async () => {
    const response = await api.get('/emissoras/estados');
    return response.data;
  },

  getEmissoraById: async (id: number): Promise<Emissora> => {
    try {
      console.log(`[EmissorasService] Attempting to getEmissoraById: ${id}.`);
      console.log('[EmissorasService] Imported api instance baseURL:', api.defaults.baseURL);
      const response = await api.get(`/emissoras/${id}`);
      console.log(`[EmissorasService] Emissora ${id} received successfully:`, response.data);
      console.log('[EmissorasService] Axios effective URL:', response.config.url);
      console.log('[EmissorasService] Axios effective baseURL:', response.config.baseURL);
      return response.data;
    } catch (error: any) {
      console.error(`[EmissorasService] Error fetching emissora ${id}:`, error);
      // Add detailed error logging as above
      if (error.response) {
        console.error('[EmissorasService] Error Response:', error.response.data);
      } else if (error.request) {
        console.error('[EmissorasService] Error Request Config:', error.config); 
      } else {
        console.error('[EmissorasService] Error Message:', error.message);
      }
      console.error('[EmissorasService] Full Error Config:', error.config);
      throw error;
    }
  },
};

export default EmissorasService; 